import {withMobile} from "@hps/hops-react";
import {SnackbarService} from "@hps/hops-react";
import {CheckoutBasketItem, OrderableTypes} from "@hps/hops-sdk-js";
import PropTypes from "prop-types";
import React from "react";

import Button from "Components/Button.js";
import PriceInput from "Components/PriceInput.js";
import Quantity from "Components/Quantity.js";
import withCheckoutBasket from "Hoc/withCheckoutBasket.js";
import CheckoutService from "Services/CheckoutService.js";

import Flex from "./Flex";


/**
 * "Add to Basket" button for Railway Products
 *
 * Shows Add/Price/Remove/Quantity based on existing basket contents
 * 
 * @package HOPS
 * @subpackage Checkout
 * @author Heron Web Ltd
 * @copyright Heritage Operations Processing Limited
 */
class ButtonAddProductToBasket extends React.PureComponent {

	controlledViaBasket = true;

	/**
	 * State
	 *
	 * @type {Object}
	 */
	state = {

		/**
		 * Price input value
		 *
		 * @type {Integer|null}
		 */
		priceInput: (this.selection?.Price || null)

	};


	/**
	 * Properties changed.
	 * 
	 * @param {Object} props
	 * @return {void}
	 */
	handleChange = props => {
		if (!this.controlledViaBasket) {
			this.props.onChange(props, this.props.item.Id);
		}
		else this.handleChangeBasket(props);
	};


	/**
	 * Properties changed - update the basket.
	 *
	 * @param {Object} props
	 * @return {void}
	 */
	handleChangeBasket = async props => {

		const item = this.basketItem;

		const createBasketItem = () => {
			return CheckoutBasketItem.construct({
				Uuid: item?.Uuid,
				OrderableType: OrderableTypes.RailwayProduct,
				Item: this.props.item,
				Price: (props.Price || this.state.priceInput || item?.Price || this.props.item.Price),
				Quantity: (props.Quantity || item?.Quantity || 1),
				VatProportion: (item?.VatProportion || this.props.item.VatProportion),
				VatRate: (item?.VatRate || this.props.item.VatRate)
			});
		};

		if (!item) {
			if (props.Price || props.Quantity) {
				try {
					await CheckoutService.addBasketItems([createBasketItem()], this.props.dispatchAdditionData);
				}
				catch (e) {
					SnackbarService.snack(e);
				}
			}
		}
		else if ((props.Quantity === 0) || (props.Price === null)) {
			try {
				await CheckoutService.removeBasketItems([item]);
			}
			catch (e) {
				SnackbarService.snack(e);
			}
		}
		else {
			try {
				await CheckoutService.updateBasketItemQuantity(createBasketItem());
			}
			catch (e) {
				SnackbarService.snack(e);
			}
		}

	};


	/**
	 * Price changed.
	 * 
	 * @param {String} Price
	 * @return {void}
	 */
	handleChangePrice = Price => {
		if (!this.controlledViaBasket) {
			this.handleChange({Price});
		}
		else this.setState({priceInput: Price});
	};


	/**
	 * Price input blurred.
	 *
	 * We clear the quantity if the user removed the price.
	 * 
	 * @return {void}
	 */
	handleChangePriceBlur = () => {
		if (!this.selection?.Price && this.selection?.Quantity) {
			this.handleChange({Quantity: 0});
		}
	};


	/**
	 * Quantity changed.
	 * 
	 * @param {Integer} Quantity
	 * @return {void}
	 */
	handleChangeQuantity = Quantity => {
		this.handleChange({Quantity});
	};


	/**
	 * Render.
	 * 
	 * @return {ReactNode}
	 */
	render() {
		return (
			<Flex>
				{this.shouldRenderPriceInput && this.renderPriceInput()}
				{this.shouldRenderAto && this.renderAto()}
				{(this.shouldRenderRfo ||this.shouldRenderQty) && <Flex alignItems="center" columnar={true} justifyContent="space-between">
					{this.shouldRenderRfo && this.renderRfo()}
					{this.shouldRenderQty && this.renderQty()}
				</Flex>}
			</Flex>
		);
	}


	/**
	 * Render "add to order" button.
	 * 
	 * @return {ReactNode}
	 */
	renderAto() {
		return (
			<Button
				disabled={!this.atoEnabled}
				label="Add to Basket"
				loading={this.props.checkoutBasketLoading}
				onClick={this.handleChangeQuantity}
				value={1}
				variant={this.props.variant} />
		);
	}


	/**
	 * Render price section input.
	 * 
	 * @return {ReactNode}
	 */
	renderPriceInput() {
		return (
			<PriceInput
				disabled={(this.disabled || (this.controlledViaBasket && !!this.selection))}
				label="Enter Amount"
				onBlur={this.handleChangePriceBlur}
				onChange={this.handleChangePrice}
				placeholder="0.00"
				value={this.selectionPrice} />
		);
	}


	/**
	 * Render quantity selector.
	 * 
	 * @return {ReactNode}
	 */
	renderQty() {
		return (
			<Quantity
				disabled={this.disabled}
				max={(this.props.item?.MaxQuantity || undefined)}
				onChange={this.handleChangeQuantity}
				value={this.selection?.Quantity}
				variant={this.props.variant} />
		);
	}


	/**
	 * Render "remove from order" link.
	 * 
	 * @return {ReactNode}
	 */
	renderRfo() {
		return (
			<Button
				label="Remove"
				loading={this.props.checkoutBasketLoading}
				onClick={this.handleChangeQuantity}
				value={0}
				variant={this.props.variant} />
		);
	}


	/**
	 * Get whether to enable the "add to order" button.
	 * 
	 * @return {Boolean}
	 */
	get atoEnabled() {
		if (this.disabled) return false;
		else if (this.props.item?.Price || this.props.item?.Price === 0) return true;
		else return (this.controlledViaBasket ? !!this.state.priceInput : this.selection?.Price);
	}


	/**
	 * Get our representation in the basket when available.
	 * 
	 * @return {Object|null}
	 */
	get basketItem() {
		return this.props.checkoutBasket.find(i => {
			const oid = (i.Item?.Id === this.props.item?.Id);
			const ot = (i.OrderableType === OrderableTypes.RailwayProduct);
			return (oid && ot);
		});
	}


	/**
	 * Get whether to disable the UI controls.
	 *
	 * @return {Boolean}
	 */
	get disabled() {
		return (this.props.disabled || (this.controlledViaBasket && this.props.checkoutBasketLoading));
	}


	/**
	 * Get whether to use the mobile view.
	 * 
	 * @return {Boolean}
	 */
	get isMobile() {
		return !this.props.bp("sm");
	}


	/**
	 * Get whether the user has to enter a price.
	 * 
	 * @return {Boolean}
	 */
	get isUserPrice() {
		return (this.props.item.Price === null);
	}


	/**
	 * Get the object defining the current user selection.
	 * 
	 * @return {Object}
	 */
	get selection() {
		if (this.controlledViaBasket) {

			const item = this.basketItem;

			if (item) {
				const {Price, Quantity} = item;
				return {Price, Quantity};
			}
			else return null;

		}
		else return this.props.selection;
	}


	/**
	 * Get the item's price in the current selection.
	 * 
	 * @return {Number}
	 */
	get selectionPrice() {
		const price = (this.controlledViaBasket ? this.state.priceInput : this.selection?.Price);
		if (!this.controlledViaBasket) return price;
		else return (price ? (price / 100) : null);
	}


	/**
	 * Get whether we should render the "add to order" button.
	 * 
	 * @return {Boolean}
	 */
	get shouldRenderAto() {
		return !this.selection?.Quantity;
	}


	/**
	 * Get whether we should render the "Enter Amount" text field.
	 * 
	 * @return {Boolean}
	 */
	get shouldRenderPriceInput() {
		return (!(this.props.disablePriceInput) && this.props.item.Price === null);
	}


	/**
	 * Get whether we should render the quantity selection.
	 * 
	 * @return {Boolean}
	 */
	get shouldRenderQty() {
		const selected = this.selection?.Quantity;
		return ((this.props.item.MaxQuantity !== 1) && selected);
	}


	/**
	 * Get whether we should render the "remove from order" link.
	 * 
	 * @return {ReactNode}
	 */
	get shouldRenderRfo() {
		/*
		 * const qty = this.selection?.Quantity;
		 * return (!this.shouldRenderAto && !this.shouldRenderQty && qty);
		 */
		return !this.shouldRenderAto;
	}


	/**
	 * Error icon
	 * 
	 * @type {ReactNode}
	 */
	static errorIcon = <div />;

	/**
	 * Image loader props.
	 * 
	 * @type {Object}
	 */
	static imgLoaderProps = {absolute: false, align: "flex-start"};


	static propTypes = {
		disablePriceInput: PropTypes.bool,
		dispatchAdditionData: PropTypes.bool,
		variant: PropTypes.string
	};

	static defaultProps = {
		disablePriceInput: false,
		dispatchAdditionData: null,
		variant: "outlined"
	};

}

export default withCheckoutBasket(withMobile(ButtonAddProductToBasket));
